<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft, ArrowRight, BookOpenCheck, Check, ChevronDown, CirclePlus, Copy, Eye, FlaskConical, GripVertical, Image, Info, Layers3, Monitor, Palette, Save, Search, Send, ShieldCheck, Sparkles, Star, Trash2, TriangleAlert, Volume2, X } from '@lucide/vue'
import { ApiError, api, deleteJson, isLocalCreatorMode, postJson } from '../api'
import SurveyQuestionRenderer from '../components/SurveyQuestionRenderer.vue'
import type { EditableProfile, SurveyDefinition, SurveyQuestion, SurveySettings } from '../types'

const route = useRoute()
const router = useRouter()
const standaloneMode = isLocalCreatorMode()
const creatorRoot = standaloneMode ? '/local-create' : '/creator'
const id = computed(() => typeof route.params.id === 'string' ? route.params.id : '')
const loading = ref(Boolean(id.value))
const saving = ref(false)
const message = ref('')
const saveState = ref<'saved' | 'dirty' | 'saving' | 'local-only'>('saved')
const lastSavedAt = ref<Date | null>(null)
const initialized = ref(false)
const activeTab = ref<'content' | 'result' | 'experience' | 'professional'>('content')
const activeQuestionIndex = ref(0)
const questionQuery = ref('')
const draggedQuestionIndex = ref<number | null>(null)
const collaborators = ref<Array<{ id: string; name: string; email: string; role: string }>>([])
const collaboratorForm = ref({ email: '', role: 'editor' })
const governanceDocuments = ref<Array<Record<string, any>>>([])
const documentForm = ref({ kind: 'copyright_license', title: '', referenceUrl: '', issuer: '' })
const psychometricReport = ref<Record<string, unknown> | null>(null)
const uploadingProfileKey = ref('')
const appeals = ref<Array<Record<string, any>>>([])
const appealForm = ref({ targetType: 'review_decision', targetId: '', message: '' })
const simulationScores = ref<Record<string, number>>({})
const previewAnswers = ref<Record<string, unknown>>({})
const fullPreviewOpen = ref(false)
const fullPreviewIndex = ref(0)
const compactPreviewOpen = ref(false)

const categories = ['人格与自我认知', '情绪与幸福感', '压力与应对', '人际与亲密关系', '学习与动机', '职业与组织', '校园与社会调查', '用户研究与活动反馈', '其他']
const accents = ['#718064', '#f1845d', '#7772c7', '#548b9a', '#a65f58']
const impactLabels = ['几乎无影响', '轻微情绪触动', '中等反思负荷', '较强情绪或判断影响', '高敏感与显著影响']
let autoSaveTimer: number | undefined
let editRevision = 0
let saveQueue = Promise.resolve()

function defaultGovernance(communityIntro = ''): NonNullable<SurveyDefinition['governance']> {
  return {
    copyrightOwner: '', license: '', validatedPopulation: '', expertReviewRequired: false, crisisProtocol: '', dataRetentionDays: 365,
    ipRightsConfirmed: false, ipSafetyConfirmed: false, ipMatchConfirmed: false, ipBiasAcknowledged: false,
    communityIntro, impactLevel: 1,
    researchSelfAssessment: { researchPurpose: '', methodBasis: '', participantImpact: '', riskMitigation: '', truthfulnessConfirmed: false },
  }
}

function normalizeGovernance(definition: SurveyDefinition, description = '') {
  const existing = definition.governance || {}
  definition.governance = {
    ...defaultGovernance(description),
    ...existing,
    communityIntro: existing.communityIntro || description,
    researchSelfAssessment: { ...defaultGovernance().researchSelfAssessment, ...existing.researchSelfAssessment },
  }
}

function normalizeExperience(definition: SurveyDefinition, type: string) {
  const defaultMode = type === 'research' || type === 'sensitive' ? 'neutral' : 'heartlight'
  definition.presentation = {
    mode: defaultMode,
    navigationMode: 'paged',
    randomizeQuestions: false,
    showProgress: true,
    positiveFeedback: defaultMode === 'heartlight',
    resultMode: 'profile',
    ...definition.presentation,
  }
  definition.presentation.mode ||= defaultMode
  definition.presentation.resultMode ||= 'profile'
  definition.experiment = {
    enabled: false,
    label: 'A/B答题界面对照实验',
    ...definition.experiment,
  }
}

const form = ref({
  title: '我的新问卷', description: '用一段清晰的说明，告诉填写者这份问卷想了解什么。', type: 'psychology', category: '人格与自我认知', riskLevel: 'low', minAge: 12, accent: '#718064', estimatedMinutes: 3,
  definition: {
    intro: '这是一段轻松的自我探索，请按照最近一段时间的真实感受作答。',
    consent: '本问卷用于自我探索与匿名统计，不构成医学诊断。你可以随时退出。',
    completionMessage: '谢谢你的认真回答，愿这份结果成为一束温柔的参考。',
    dimensions: [{ key: 'energy', label: '能量感', color: '#718064' }],
    questions: [
      { id: crypto.randomUUID(), type: 'slider', prompt: '面对最近的生活，我能感受到稳定的前进力量。', required: true, dimension: 'energy', leftLabel: '不太符合', rightLabel: '很符合' },
      { id: crypto.randomUUID(), type: 'scale', prompt: '我愿意为自己留出恢复精力的时间。', required: true, dimension: 'energy', min: 1, max: 5, leftLabel: '不太符合', rightLabel: '很符合' },
    ] as SurveyQuestion[],
    profiles: [
      { key: 'light', title: '微光探索者', emoji: '✦', color: '#718064', description: '你正在认真听见自己的节奏，并愿意继续向前探索。', tags: ['觉察', '成长', '开放'], advice: '为今天选择一个十五分钟能够完成的小行动。', targets: { energy: 70 } },
      { key: 'rest', title: '温柔修复师', emoji: '◌', color: '#7772c7', description: '你正在学习保护能量，停下来也是一种重要能力。', tags: ['修复', '柔软', '有韧性'], advice: '减少一个非必要任务，为真实的休息留出位置。', targets: { energy: 35 } },
    ] as EditableProfile[],
    experiment: { enabled: false, label: 'A/B答题界面对照实验' },
    presentation: { mode: 'heartlight', navigationMode: 'paged', randomizeQuestions: false, showProgress: true, positiveFeedback: true, resultMode: 'profile' },
    governance: defaultGovernance('一份轻松、有回应的自我探索，完成后可以获得属于此刻的温柔反馈。'),
  } as SurveyDefinition,
  settings: { quota: null, startsAt: null, endsAt: null, responseLimit: 'once', requireLogin: false, allowSaveProgress: true, password: null, passwordProtected: false, listingMode: 'public' } as SurveySettings,
})

const backupKey = computed(() => {
  if (id.value) return `lumi-builder-draft-${id.value}`
  const template = typeof route.query.template === 'string' ? `template-${route.query.template}` : 'new'
  return `lumi-builder-draft-${template}`
})
const saveStatusText = computed(() => {
  if (saveState.value === 'saving') return '正在自动保存…'
  if (saveState.value === 'dirty') return '有修改，等待自动保存'
  if (saveState.value === 'local-only') return '已备份到本机，等待服务器同步'
  if (lastSavedAt.value) return `已保存 ${lastSavedAt.value.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`
  return `草稿会自动保存在${standaloneMode ? '当前浏览器' : '当前设备与服务器'}`
})
const resultMode = computed({
  get: () => form.value.definition.presentation?.resultMode ?? 'profile',
  set: (value: 'profile' | 'scores_only') => { form.value.definition.presentation = { ...form.value.definition.presentation, resultMode: value } },
})
const strictIpReview = computed(() => form.value.type === 'research' || form.value.type === 'sensitive')
const experienceMode = computed({
  get: () => form.value.definition.presentation?.mode ?? (strictIpReview.value ? 'neutral' : 'heartlight'),
  set: (value: 'heartlight' | 'neutral') => {
    form.value.definition.presentation = {
      ...form.value.definition.presentation,
      mode: value,
      positiveFeedback: value === 'heartlight',
    }
  },
})
const communityZone = computed(() => strictIpReview.value ? '量域馆' : '心光馆')
const publicationDestination = computed(() => form.value.settings.listingMode === 'unlisted' ? '不公开专属链接' : communityZone.value)
const currentQuestion = computed(() => form.value.definition.questions[activeQuestionIndex.value] || form.value.definition.questions[0])
const defaultFillMode = computed({
  get: () => form.value.definition.presentation?.navigationMode ?? 'paged',
  set: (value: 'paged' | 'scroll') => {
    form.value.definition.presentation = { ...form.value.definition.presentation, navigationMode: value }
  },
})
function previewRuleMatches(answer: unknown, operator: string, expected: unknown) {
  if (operator === 'equals') return answer === expected
  if (operator === 'not_equals') return answer !== expected
  if (operator === 'includes') return Array.isArray(answer) ? answer.includes(expected) : String(answer ?? '').includes(String(expected ?? ''))
  if (operator === 'not_includes') return Array.isArray(answer) ? !answer.includes(expected) : !String(answer ?? '').includes(String(expected ?? ''))
  if (operator === 'greater_than') return Number(answer) > Number(expected)
  if (operator === 'less_than') return Number(answer) < Number(expected)
  if (operator === 'answered') return answer !== undefined && answer !== null && answer !== ''
  if (operator === 'not_answered') return answer === undefined || answer === null || answer === ''
  return false
}
const previewVisibleQuestions = computed(() => form.value.definition.questions.filter((question) => {
  if (!question.visibility?.rules.length) return true
  const results = question.visibility.rules.map((rule) => previewRuleMatches(previewAnswers.value[rule.questionId], rule.operator, rule.value))
  return question.visibility.mode === 'all' ? results.every(Boolean) : results.some(Boolean)
}))
const fullPreviewQuestion = computed(() => previewVisibleQuestions.value[fullPreviewIndex.value] || previewVisibleQuestions.value[0])
const filteredQuestionIndexes = computed(() => {
  const query = questionQuery.value.trim().toLocaleLowerCase('zh-CN')
  return form.value.definition.questions
    .map((question, index) => ({ question, index }))
    .filter(({ question, index }) => !query || `q${index + 1} ${question.prompt}`.toLocaleLowerCase('zh-CN').includes(query))
    .map(({ index }) => index)
})
function questionOutlineSummary(question: SurveyQuestion) {
  const prompt = question.prompt.trim()
  if (!prompt) return '尚未填写题干'
  const dividerIndex = prompt.indexOf('｜')
  const prefix = dividerIndex > 0 && dividerIndex <= 10 ? prompt.slice(0, dividerIndex).trim() : ''
  const body = dividerIndex > 0 && dividerIndex <= 10 ? prompt.slice(dividerIndex + 1).trim() : prompt
  const option = body.match(/选项\s*([A-Za-zＡ-Ｚａ-ｚ0-9]+)\s*[：:]/)?.[1]
  const context = body
    .replace(/\s*选项\s*[A-Za-zＡ-Ｚａ-ｚ0-9]+\s*[：:][\s\S]*$/, '')
    .split(/[。！？!?]/)[0]
    .trim()
  const shortContext = context.length > 22 ? `${context.slice(0, 22)}…` : context
  return `${prefix ? `${prefix}｜` : ''}${shortContext || body.slice(0, 22)}${option ? ` · 选项 ${option}` : ''}`
}
const sourceMappingSummary = computed(() => {
  const source = form.value.definition.sourceImport
  if (!source) return null
  const mappingByQuestion = new Map(source.mappings.map((mapping) => [mapping.questionId, mapping]))
  const orderedMappings = form.value.definition.questions.flatMap((question) => {
    const mapping = mappingByQuestion.get(question.id)
    return mapping ? [mapping] : []
  })
  const orderPreserved = orderedMappings.every((mapping, index) => index === 0 || mapping.sourceIndex >= orderedMappings[index - 1].sourceIndex)
  const coverage = form.value.definition.questions.length
    ? Math.round(orderedMappings.length / form.value.definition.questions.length * 100)
    : 0
  return {
    source,
    mappingByQuestion,
    orderedMappings,
    coverage,
    orderPreserved,
    missing: form.value.definition.questions.filter((question) => !mappingByQuestion.has(question.id)),
  }
})
const builderAuditIssues = computed(() => {
  const issues: Array<{ code: string; message: string; questionIndex?: number }> = []
  const prompts = new Map<string, number>()
  form.value.definition.questions.forEach((question, index) => {
    const prompt = question.prompt.trim()
    if (!prompt) issues.push({ code: 'empty_prompt', message: `Q${index + 1} 还没有题干。`, questionIndex: index })
    const normalizedPrompt = prompt.replace(/\s+/g, '').toLocaleLowerCase('zh-CN')
    if (normalizedPrompt && prompts.has(normalizedPrompt)) issues.push({ code: 'duplicate_prompt', message: `Q${index + 1} 与 Q${(prompts.get(normalizedPrompt) ?? 0) + 1} 题干重复。`, questionIndex: index })
    else if (normalizedPrompt) prompts.set(normalizedPrompt, index)
    if (['single', 'multi', 'ranking'].includes(question.type) && (question.options?.length ?? 0) < 2) issues.push({ code: 'missing_options', message: `Q${index + 1} 至少需要两个选项。`, questionIndex: index })
    if (['matrix_single', 'matrix_multi'].includes(question.type) && (!(question.rows?.length) || !(question.columns?.length))) issues.push({ code: 'missing_matrix', message: `Q${index + 1} 的矩阵行或列为空。`, questionIndex: index })
    for (const rule of question.visibility?.rules ?? []) {
      const sourceIndex = form.value.definition.questions.findIndex((item) => item.id === rule.questionId)
      if (sourceIndex < 0 || sourceIndex >= index) issues.push({ code: 'logic_break', message: `Q${index + 1} 的显示条件必须引用它之前存在的题目。`, questionIndex: index })
    }
  })
  return issues
})
const profileSimulation = computed(() => {
  const dimensions = form.value.definition.dimensions
  const ranked = (form.value.definition.profiles as EditableProfile[]).map((profile) => {
    const squared = dimensions.reduce((sum, dimension) => {
      const score = simulationScores.value[dimension.key] ?? 50
      const target = Number(profile.targets[dimension.key] ?? 50)
      return sum + (score - target) ** 2
    }, 0)
    return { profile, distance: Math.round(Math.sqrt(squared / Math.max(dimensions.length, 1))) }
  }).sort((a, b) => a.distance - b.distance)
  const winner = ranked[0]
  const gap = ranked.length > 1 ? ranked[1].distance - (winner?.distance ?? 0) : null
  return { ranked, winner, gap, clear: gap === null || gap >= 12 }
})

function simulationScore(key: string) { return simulationScores.value[key] ?? 50 }
function setSimulationScore(key: string, value: string | number) { simulationScores.value[key] = Number(value) }

function serializableCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function persistLocalDraft(key = backupKey.value) {
  try {
    localStorage.setItem(key, JSON.stringify({ form: serializableCopy(form.value), savedAt: new Date().toISOString() }))
  } catch { /* local storage may be unavailable in private mode */ }
}

function restoreLocalDraft(serverUpdatedAt?: string) {
  try {
    const raw = localStorage.getItem(backupKey.value)
    if (!raw) return false
    const saved = JSON.parse(raw) as { form?: typeof form.value; savedAt?: string }
    if (!saved.form || typeof saved.form !== 'object') return false
    const localTime = new Date(saved.savedAt || 0).getTime()
    const serverTime = serverUpdatedAt ? new Date(serverUpdatedAt).getTime() : 0
    if (serverUpdatedAt && localTime <= serverTime) {
      localStorage.removeItem(backupKey.value)
      return false
    }
    form.value = saved.form
    normalizeExperience(form.value.definition, form.value.type)
    normalizeGovernance(form.value.definition, form.value.description)
    saveState.value = 'local-only'
    return true
  } catch { return false }
}

function handlePageHide() {
  if (saveState.value !== 'saved') persistLocalDraft()
}

function handleWorkspaceKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return
  if (fullPreviewOpen.value) fullPreviewOpen.value = false
  else if (compactPreviewOpen.value) compactPreviewOpen.value = false
}

onMounted(async () => {
  try {
    if (!id.value) {
      if (typeof route.query.template === 'string') {
        const template = await api<any>(`/api/templates/${route.query.template}`)
        form.value.title = `${template.name}副本`
        form.value.description = template.description
        form.value.type = template.type
        form.value.category = template.category
        form.value.definition = template.definition
        normalizeExperience(form.value.definition, form.value.type)
        normalizeGovernance(form.value.definition, form.value.description)
      }
      if (restoreLocalDraft()) message.value = '已恢复上次未同步的编辑内容。'
    } else {
      const survey = await api<any>(`/api/creator/surveys/${id.value}`)
      const definition = survey.definition as SurveyDefinition
      normalizeExperience(definition, survey.type)
      normalizeGovernance(definition, survey.description)
      const settings = survey.settings || {}
      form.value = { title: survey.title, description: survey.description, type: survey.type, category: survey.category, riskLevel: survey.riskLevel, minAge: survey.minAge, accent: survey.accent, estimatedMinutes: survey.estimatedMinutes, definition, settings: { quota: settings.quota, startsAt: settings.startsAt ? String(settings.startsAt).slice(0, 16) : null, endsAt: settings.endsAt ? String(settings.endsAt).slice(0, 16) : null, responseLimit: settings.responseLimit || 'once', requireLogin: settings.requireLogin ?? false, allowSaveProgress: settings.allowSaveProgress ?? true, password: null, passwordProtected: settings.passwordProtected, listingMode: settings.listingMode === 'unlisted' ? 'unlisted' : 'public' } }
      if (!standaloneMode) await loadProfessionalData()
      if (restoreLocalDraft(survey.updatedAt)) message.value = '已恢复尚未同步到服务器的编辑内容。'
    }
  } catch (err) {
    message.value = err instanceof ApiError ? err.message : '打开草稿失败，请稍后重试。'
  } finally {
    loading.value = false
    await nextTick()
    initialized.value = true
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('keydown', handleWorkspaceKeydown)
  }
})

function addQuestion(type: SurveyQuestion['type'] = 'slider') {
  form.value.definition.questions.push({
    id: crypto.randomUUID(), type, prompt: '输入一个清晰、中立的问题', required: true,
    dimension: form.value.definition.dimensions[0]?.key, min: 1, max: 5, leftLabel: '不太符合', rightLabel: '很符合',
    options: type === 'single' || type === 'multi' || type === 'ranking' ? [{ value: 'a', label: '选项 A' }, { value: 'b', label: '选项 B' }] : undefined,
    rows: type.startsWith('matrix_') ? [{ value: 'r1', label: '题项 1' }, { value: 'r2', label: '题项 2' }] : undefined,
    columns: type.startsWith('matrix_') ? [{ value: '1', label: '不符合', score: 0 }, { value: '2', label: '一般', score: 50 }, { value: '3', label: '符合', score: 100 }] : undefined,
  })
  activeQuestionIndex.value = form.value.definition.questions.length - 1
}

function duplicateQuestion(index: number) {
  form.value.definition.questions.splice(index + 1, 0, { ...serializableCopy(form.value.definition.questions[index]), id: crypto.randomUUID() })
  activeQuestionIndex.value = index + 1
}

function removeQuestion(index: number) {
  if (form.value.definition.questions.length <= 1) {
    message.value = '问卷至少需要保留一道题；如需清空内容，可先添加一题再删除原题。'
    return
  }
  const label = `Q${index + 1}`
  if (!window.confirm(`确定删除 ${label} 吗？删除后无法恢复。引用该题目的跳题条件会一并清除。`)) return
  const removed = form.value.definition.questions[index]
  form.value.definition.questions.splice(index, 1)
  form.value.definition.questions.forEach((question) => {
    if (question.visibility?.rules.some((rule) => rule.questionId === removed.id)) question.visibility = undefined
  })
  activeQuestionIndex.value = Math.min(index, form.value.definition.questions.length - 1)
  message.value = `${label} 已删除。`
}

function selectQuestion(index: number) {
  activeQuestionIndex.value = Math.max(0, Math.min(index, form.value.definition.questions.length - 1))
}

function setPreviewAnswer(question: SurveyQuestion, value: unknown) {
  previewAnswers.value[question.id] = value
}

function openFullPreview() {
  const selectedId = currentQuestion.value?.id
  const visibleIndex = previewVisibleQuestions.value.findIndex((question) => question.id === selectedId)
  fullPreviewIndex.value = Math.max(0, visibleIndex)
  compactPreviewOpen.value = false
  fullPreviewOpen.value = true
}

function moveFullPreview(direction: -1 | 1) {
  fullPreviewIndex.value = Math.max(0, Math.min(previewVisibleQuestions.value.length - 1, fullPreviewIndex.value + direction))
}

function dropQuestion(targetIndex: number) {
  const sourceIndex = draggedQuestionIndex.value
  draggedQuestionIndex.value = null
  if (sourceIndex === null || sourceIndex === targetIndex) return
  const [question] = form.value.definition.questions.splice(sourceIndex, 1)
  form.value.definition.questions.splice(targetIndex, 0, question)
  activeQuestionIndex.value = targetIndex
}

watch(() => form.value.definition.questions.length, (length) => {
  if (activeQuestionIndex.value >= length) activeQuestionIndex.value = Math.max(0, length - 1)
})

function optionText(question: SurveyQuestion) { return question.options?.map((item) => item.label).join('\n') || '' }
function setOptions(question: SurveyQuestion, value: string) {
  question.options = value.split('\n').map((label, index) => ({ value: `o${index + 1}`, label: label.trim() })).filter((item) => item.label)
}
function listText(items: SurveyQuestion['rows']) { return items?.map((item) => item.label).join('\n') || '' }
function setList(question: SurveyQuestion, key: 'rows' | 'columns', value: string) {
  question[key] = value.split('\n').map((label, index) => ({ value: `${key === 'rows' ? 'r' : 'c'}${index + 1}`, label: label.trim(), ...(key === 'columns' ? { score: value.split('\n').length <= 1 ? 50 : Math.round(index / (value.split('\n').length - 1) * 100) } : {}) })).filter((item) => item.label)
}
function setVisibility(question: SurveyQuestion, sourceId: string) {
  question.visibility = sourceId ? { mode: 'all', rules: [{ questionId: sourceId, operator: 'equals', value: '' }] } : undefined
}
function addVisibilityRule(question: SurveyQuestion) {
  const source = form.value.definition.questions.slice(0, activeQuestionIndex.value)[0]
  if (!source) return
  question.visibility ||= { mode: 'all', rules: [] }
  question.visibility.rules.push({ questionId: source.id, operator: 'equals', value: '' })
}
function removeVisibilityRule(question: SurveyQuestion, index: number) {
  if (!question.visibility) return
  question.visibility.rules.splice(index, 1)
  if (!question.visibility.rules.length) question.visibility = undefined
}
function ruleQuestionNumber(questionId: string) {
  const index = form.value.definition.questions.findIndex((question) => question.id === questionId)
  return index >= 0 ? `Q${index + 1}` : '缺失题目'
}
function ruleOperatorLabel(operator: string) {
  return ({ equals: '等于', not_equals: '不等于', includes: '包含', not_includes: '不包含', greater_than: '大于', less_than: '小于', answered: '已回答', not_answered: '未回答' } as Record<string, string>)[operator] || operator
}
function toggleCrisis(question: SurveyQuestion, enabled: boolean) {
  question.crisisTriggers = enabled ? [{ operator: 'greater_or_equal', value: question.max ?? 4, severity: 'urgent', code: 'CREATOR_DEFINED_RISK' }] : undefined
}

function addDimension() {
  const n = form.value.definition.dimensions.length + 1
  const key = `dimension_${n}`
  form.value.definition.dimensions.push({ key, label: `维度 ${n}`, color: accents[(n - 1) % accents.length] })
  ;(form.value.definition.profiles as EditableProfile[]).forEach((profile) => { profile.targets[key] = 50 })
}

function removeDimension(index: number) {
  if (form.value.definition.dimensions.length <= 1) {
    message.value = '至少需要保留一个计分维度；如需不计分，请把题目的“所属维度”改为“不计分”。'
    return
  }
  const removed = form.value.definition.dimensions[index]
  const affected = form.value.definition.questions.filter((question) => question.dimension === removed.key).length
  const note = affected ? `有 ${affected} 道题会改为“不计分”。` : ''
  if (!window.confirm(`确定删除维度“${removed.label}”吗？${note} 此操作无法恢复。`)) return
  form.value.definition.dimensions.splice(index, 1)
  form.value.definition.questions.forEach((question) => {
    if (question.dimension === removed.key) question.dimension = undefined
  })
  ;(form.value.definition.profiles as EditableProfile[]).forEach((profile) => { delete profile.targets[removed.key] })
  delete simulationScores.value[removed.key]
  message.value = `维度“${removed.label}”已删除。`
}

function addProfile() {
  const n = form.value.definition.profiles.length + 1
  const key = `profile_${crypto.randomUUID().slice(0, 8)}`
  const targets = Object.fromEntries(form.value.definition.dimensions.map((dimension) => [dimension.key, 50]))
  form.value.definition.profiles.push({ key, title: `结果角色 ${n}`, emoji: '✦', color: accents[(n - 1) % accents.length], description: '用中立、尊重的语言描述这一结果。', tags: ['探索'], advice: '给填写者一个温和且可执行的建议。', targets, matchRationale: '' } as EditableProfile)
}

function profileImage(profile: EditableProfile) {
  return profile.imageUrl || (profile.imageAssetId ? `/api/profile-images/${profile.imageAssetId}` : '')
}

async function uploadProfileImage(profile: EditableProfile, event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  if (standaloneMode) { message.value = '单文件版没有图片后端；请在服务器完整版中上传角色图片。'; return }
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { message.value = '角色图片仅支持 JPG、PNG 或 WebP。'; return }
  if (file.size > 5 * 1024 * 1024) { message.value = '角色图片不能超过 5 MB。'; return }
  uploadingProfileKey.value = profile.key
  try {
    await save(false)
    if (!id.value) throw new Error('草稿尚未同步到服务器。')
    const params = new URLSearchParams({ profileKey: profile.key, filename: file.name })
    const response = await api<{ asset: { id: string; imageUrl: string } }>(`/api/creator/surveys/${id.value}/profile-images?${params}`, { method: 'POST', body: file, headers: { 'Content-Type': file.type } })
    profile.imageAssetId = response.asset.id
    profile.imageUrl = response.asset.imageUrl
    await save(false)
    message.value = '角色图片已上传并保存到问卷资产库。'
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '角色图片上传失败，请稍后重试。'
  } finally { uploadingProfileKey.value = '' }
}

async function removeProfileImage(profile: EditableProfile, skipConfirmation = false) {
  if (!skipConfirmation && !window.confirm(`确定从“${profile.title}”移除这张角色图片吗？已发布的旧版本不会受影响。`)) return false
  if (profile.imageAssetId && id.value && !standaloneMode) {
    try { await deleteJson(`/api/creator/surveys/${id.value}/profile-images/${profile.imageAssetId}`) }
    catch (error) { message.value = error instanceof ApiError ? error.message : '图片移除失败。'; return false }
  }
  profile.imageAssetId = null
  profile.imageUrl = undefined
  message.value = '已从当前草稿移除图片；已发布旧版本仍保留原图。'
  return true
}

async function removeProfile(index: number) {
  const profile = form.value.definition.profiles[index] as EditableProfile
  if (!window.confirm(`确定删除结果角色“${profile.title}”吗？角色描述、建议和匹配设置将一并移除。`)) return
  if (profile.imageAssetId && !await removeProfileImage(profile, true)) return
  form.value.definition.profiles.splice(index, 1)
  message.value = `结果角色“${profile.title}”已删除。`
}

function isoOrNull(value: string | null | undefined) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function validationMessage(error: unknown) {
  if (!(error instanceof ApiError)) return '提交审核失败。'
  const payload = error.details as { details?: Array<{ message?: string; path?: Array<string | number> }> } | undefined
  const issue = payload?.details?.[0]
  if (!issue) return error.message
  const path = issue.path?.join('.') || ''
  if (path === 'title') return '请填写至少 2 个字的问卷标题。'
  if (path.includes('questions') && path.endsWith('prompt')) return '请补全所有题目内容。'
  if (path.includes('dimensions')) return '请补全计分维度信息。'
  if (path.includes('profiles')) return '请补全结果档案信息。'
  return issue.message || error.message
}

async function performSave(goPublish = false, automatic = false) {
  saving.value = true
  saveState.value = 'saving'
  if (!automatic) message.value = ''
  const revisionAtStart = editRevision
  const previousBackupKey = backupKey.value
  persistLocalDraft(previousBackupKey)
  try {
    let surveyId = id.value
    const payload: any = serializableCopy(form.value)
    if (!payload.settings.password) delete payload.settings.password
    payload.settings.startsAt = isoOrNull(payload.settings.startsAt)
    payload.settings.endsAt = isoOrNull(payload.settings.endsAt)
    if (surveyId) {
      await api(`/api/creator/surveys/${surveyId}`, {
        method: 'PUT', body: JSON.stringify(payload),
        headers: automatic ? { 'x-lumi-autosave': '1' } : {},
      })
    }
    else {
      const created = await postJson<{ id: string }>('/api/creator/surveys', payload)
      surveyId = created.id
      await router.replace(`${creatorRoot}/${surveyId}/edit`)
    }

    lastSavedAt.value = new Date()
    if (revisionAtStart === editRevision) {
      saveState.value = 'saved'
      localStorage.removeItem(previousBackupKey)
      localStorage.removeItem(backupKey.value)
    } else {
      saveState.value = 'dirty'
      persistLocalDraft()
    }

    if (goPublish) {
      try {
        const result = await postJson<{ status: string; version: number }>(`/api/creator/surveys/${surveyId}/submit`, {})
        message.value = `保存成功，V${result.version} 已申请公开并进入站长人工审核。`
      } catch (err) {
        message.value = `草稿已经完整保存，但暂不能提交审核：${validationMessage(err)}`
      }
    } else if (!automatic) message.value = '草稿已完整保存，未完成的内容也不会丢失。'
  } catch (err) {
    saveState.value = 'local-only'
    persistLocalDraft()
    if (!automatic) message.value = `内容已备份到当前设备，服务器暂未同步：${err instanceof ApiError ? err.message : '网络连接失败。'}`
  }
  finally { saving.value = false }
}

function save(goPublish = false, automatic = false) {
  window.clearTimeout(autoSaveTimer)
  const job = () => performSave(goPublish, automatic)
  saveQueue = saveQueue.then(job, job)
  return saveQueue
}

watch(form, () => {
  if (!initialized.value) return
  editRevision += 1
  saveState.value = 'dirty'
  persistLocalDraft()
  window.clearTimeout(autoSaveTimer)
  autoSaveTimer = window.setTimeout(() => void save(false, true), 1_200)
}, { deep: true })

onBeforeUnmount(() => {
  initialized.value = false
  window.clearTimeout(autoSaveTimer)
  window.removeEventListener('pagehide', handlePageHide)
  window.removeEventListener('keydown', handleWorkspaceKeydown)
  if (saveState.value !== 'saved') persistLocalDraft()
})

async function loadProfessionalData() {
  if (!id.value) return
  const [members, governance, appealRows] = await Promise.all([
    api<Array<{ id: string; name: string; email: string; role: string }>>(`/api/creator/surveys/${id.value}/collaborators`),
    api<{ documents: Array<Record<string, any>> }>(`/api/creator/surveys/${id.value}/governance`),
    api<Array<Record<string, any>>>(`/api/creator/surveys/${id.value}/appeals`),
  ])
  collaborators.value = members
  governanceDocuments.value = governance.documents
  appeals.value = appealRows
}
async function addCollaborator() {
  await postJson(`/api/creator/surveys/${id.value}/collaborators`, collaboratorForm.value)
  collaboratorForm.value.email = ''; await loadProfessionalData(); message.value = '协作者已更新。'
}
async function removeCollaborator(userId: string, name: string) {
  if (!window.confirm(`确定移除协作者“${name}”吗？对方将立即失去该问卷的访问权限。`)) return
  await deleteJson(`/api/creator/surveys/${id.value}/collaborators/${userId}`)
  await loadProfessionalData()
  message.value = `已移除协作者“${name}”。`
}
async function addDocument() {
  const payload = { ...documentForm.value, referenceUrl: documentForm.value.referenceUrl || undefined, issuer: documentForm.value.issuer || undefined }
  await postJson(`/api/creator/surveys/${id.value}/governance/documents`, payload)
  documentForm.value.title = ''; documentForm.value.referenceUrl = ''; await loadProfessionalData(); message.value = '治理材料已提交审核。'
}
async function removeDocument(document: Record<string, any>) {
  if (!window.confirm(`确定删除材料“${document.title}”吗？删除后无法恢复。`)) return
  try {
    await deleteJson(`/api/creator/surveys/${id.value}/governance/documents/${document.id}`)
    await loadProfessionalData()
    message.value = `材料“${document.title}”已删除。`
  } catch (error) {
    message.value = error instanceof ApiError ? error.message : '删除材料失败。'
  }
}
async function analyzePsychometrics() {
  psychometricReport.value = await postJson(`/api/creator/surveys/${id.value}/psychometrics`, {})
}
async function submitAppeal() {
  if (!appealForm.value.message.trim()) return
  await postJson(`/api/creator/surveys/${id.value}/appeals`, {
    targetType: appealForm.value.targetType,
    targetId: appealForm.value.targetId || undefined,
    message: appealForm.value.message,
  })
  appealForm.value.message = ''
  appealForm.value.targetId = ''
  await loadProfessionalData()
  message.value = '申诉已提交，管理员会在后台处理。'
}
</script>

<template>
  <div v-if="loading" class="page-loading"><span class="loader-orb"/><p>正在打开创作台…</p></div>
  <div v-else class="builder-page">
    <header class="builder-header">
          <RouterLink :to="creatorRoot" class="icon-button" title="返回创作者中心" aria-label="返回创作者中心"><ArrowLeft/></RouterLink>
      <div><input v-model="form.title" class="builder-title-input" maxlength="80" aria-label="问卷标题"/><span :class="`save-status ${saveState}`"><i/>{{ saveStatusText }}</span></div>
      <div class="builder-header-actions">
        <button
          type="button"
          class="button button-ghost builder-preview-toggle"
          aria-controls="builder-live-preview"
          :aria-expanded="compactPreviewOpen"
          @click="compactPreviewOpen = !compactPreviewOpen"
        ><Eye/><span>实时预览</span></button>
        <button class="button button-ghost builder-save-button" @click="save(false)"><Save/>{{ saving ? '保存中' : '保存草稿' }}</button>
        <button v-if="!standaloneMode" class="button button-dark" @click="save(true)"><Send/>申请站长审核</button>
        <span v-else class="builder-local-only"><ShieldCheck/>本地草稿不会上传；保存后可回到工作台导出 JSON 备份。</span>
      </div>
    </header>

    <div class="builder-shell">
      <aside class="builder-sidebar">
        <p class="eyebrow">BUILD FLOW</p>
        <button :class="{ active: activeTab === 'content' }" @click="activeTab = 'content'"><Layers3/>内容与题目<span>{{ form.definition.questions.length }}</span></button>
        <button :class="{ active: activeTab === 'result' }" @click="activeTab = 'result'"><Image/>维度与IP<span>{{ resultMode === 'profile' ? form.definition.profiles.length : '无IP' }}</span></button>
        <button :class="{ active: activeTab === 'experience' }" @click="activeTab = 'experience'"><Palette/>体验与发布</button>
        <button :class="{ active: activeTab === 'professional' }" @click="activeTab = 'professional'"><ShieldCheck/>高级与协作</button>
        <div v-if="activeTab === 'content'" class="builder-question-outline">
          <label><Search/><input v-model="questionQuery" type="search" placeholder="搜索题号或题干" aria-label="搜索问卷题目"/></label>
          <div>
            <button
              v-for="index in filteredQuestionIndexes"
              :key="form.definition.questions[index].id"
              type="button"
              draggable="true"
              :title="`Q${String(index + 1).padStart(2, '0')} · ${form.definition.questions[index].prompt || '尚未填写题干'}`"
              :class="{ active: activeQuestionIndex === index, warning: !form.definition.questions[index].prompt.trim() }"
              @dragstart="draggedQuestionIndex = index"
              @dragover.prevent
              @drop.prevent="dropQuestion(index)"
              @dragend="draggedQuestionIndex = null"
              @click="selectQuestion(index)"
            >
              <b>Q{{ String(index + 1).padStart(2, '0') }}</b>
              <span>{{ questionOutlineSummary(form.definition.questions[index]) }}</span>
            </button>
          </div>
          <button type="button" class="outline-add-question" @click="addQuestion()"><CirclePlus/>添加题目</button>
        </div>
        <div class="builder-tip"><Info/><p>发布后的修改会生成新版本，旧数据仍关联旧版本。</p></div>
      </aside>

      <main class="builder-canvas">
        <p v-if="message" class="dashboard-message sticky-message">{{ message }}</p>
        <template v-if="activeTab === 'content'">
          <details class="builder-section builder-disclosure overview-editor" :open="!id">
            <summary class="builder-section-title"><span>01</span><div><h2>问卷基本信息</h2><p>{{ form.type === 'research' ? '研究实验' : form.type === 'sensitive' ? '敏感心理研究' : form.type === 'general' ? '一般问卷' : '心理探索' }} · {{ form.category }} · 约 {{ form.estimatedMinutes }} 分钟</p></div><ChevronDown/></summary>
            <div class="form-grid builder-disclosure-body">
              <label class="field-label wide"><span>一句话介绍</span><textarea v-model="form.description" rows="2" maxlength="500"/></label>
              <label class="field-label"><span>问卷类型</span><select v-model="form.type"><option value="psychology">心理探索</option><option value="research">研究实验</option><option value="general">一般问卷</option><option value="sensitive">敏感心理研究</option></select></label>
              <label class="field-label"><span>分类</span><select v-model="form.category"><option v-for="item in categories" :key="item">{{ item }}</option></select></label>
              <label class="field-label"><span>最低年龄</span><input v-model.number="form.minAge" type="number" min="6" max="100"/></label>
              <label class="field-label"><span>预计分钟</span><input v-model.number="form.estimatedMinutes" type="number" min="1" max="120"/></label>
              <label class="field-label wide"><span>开始前引导</span><textarea v-model="form.definition.intro" rows="3"/></label>
              <label class="field-label wide"><span>知情同意说明</span><textarea v-model="form.definition.consent" rows="3"/></label>
            </div>
          </details>

          <details v-if="sourceMappingSummary" class="builder-section builder-disclosure source-mapping-panel">
            <summary class="builder-section-title">
              <span>源</span>
              <div><h2>原文件—问卷映射</h2><p>{{ sourceMappingSummary.source.fileName }} · 覆盖 {{ sourceMappingSummary.coverage }}% · {{ sourceMappingSummary.orderPreserved ? '题目顺序保持' : '题目顺序已变化' }}</p></div>
              <b :class="['source-confidence-chip', sourceMappingSummary.source.confidence || 'medium']">{{ sourceMappingSummary.source.confidence === 'high' ? '高置信度' : sourceMappingSummary.source.confidence === 'low' ? '低置信度' : '中等置信度' }}</b>
              <ChevronDown/>
            </summary>
            <div class="builder-disclosure-body source-mapping-body">
              <div :class="['source-restore-warning', { danger: !sourceMappingSummary.orderPreserved || sourceMappingSummary.missing.length }]">
                <TriangleAlert/>
                <div>
                  <strong>{{ sourceMappingSummary.orderPreserved && !sourceMappingSummary.missing.length ? '当前字段和题目顺序可以按源格式还原' : '当前改动可能影响源格式完整还原' }}</strong>
                  <p>修改题干或选项不会丢失答案，但移动、删除或新增题目后，请在导出前重新核对字段映射。系统会保留原模板，不会覆盖源文件。</p>
                </div>
              </div>
              <div class="source-mapping-stats">
                <span><b>{{ sourceMappingSummary.orderedMappings.length }}</b>已映射题目</span>
                <span><b>{{ sourceMappingSummary.missing.length }}</b>新增或未确认题目</span>
                <span><b>{{ sourceMappingSummary.source.warnings.length }}</b>导入提示</span>
              </div>
              <div class="source-mapping-table-wrap">
                <table class="source-mapping-table">
                  <thead><tr><th>当前题号</th><th>当前题目</th><th>源位置</th><th>源字段</th><th>状态</th></tr></thead>
                  <tbody>
                    <tr v-for="(questionItem, questionIndex) in form.definition.questions" :key="questionItem.id">
                      <td>Q{{ questionIndex + 1 }}</td>
                      <td>{{ questionItem.prompt }}</td>
                      <td>{{ sourceMappingSummary.mappingByQuestion.get(questionItem.id)?.sourceSheet || '—' }}<template v-if="sourceMappingSummary.mappingByQuestion.get(questionItem.id)?.sourceRow"> · 行 {{ sourceMappingSummary.mappingByQuestion.get(questionItem.id)?.sourceRow }}</template></td>
                      <td>{{ sourceMappingSummary.mappingByQuestion.get(questionItem.id)?.sourceColumn || sourceMappingSummary.mappingByQuestion.get(questionItem.id)?.sourceLabel || '—' }}</td>
                      <td><span :class="sourceMappingSummary.mappingByQuestion.has(questionItem.id) ? 'mapped' : 'unmapped'">{{ sourceMappingSummary.mappingByQuestion.has(questionItem.id) ? '已映射' : '需确认' }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <ul v-if="sourceMappingSummary.source.warnings.length" class="source-warning-list"><li v-for="warning in sourceMappingSummary.source.warnings" :key="warning">{{ warning }}</li></ul>
            </div>
          </details>

          <section class="builder-section question-editor-section">
            <div class="builder-section-title"><span>02</span><div><h2>题目流程</h2><p>左侧选择题目，中间一次只编辑一道，避免长问卷铺满页面。</p></div><button class="button button-small" @click="addQuestion()"><CirclePlus/>添加题目</button></div>
            <div :class="['builder-audit-summary', { clean: !builderAuditIssues.length }]">
              <ShieldCheck v-if="!builderAuditIssues.length"/><TriangleAlert v-else/>
              <div><strong>{{ builderAuditIssues.length ? `发现 ${builderAuditIssues.length} 项发布前问题` : '题目结构检查通过' }}</strong><p>{{ builderAuditIssues.length ? '点击问题可直接定位；保存草稿不受影响，申请审核前需要处理。' : '没有发现空题干、缺少选项、重复题干或逻辑断路。' }}</p></div>
              <details v-if="builderAuditIssues.length"><summary>查看问题</summary><ul><li v-for="(issue, issueIndex) in builderAuditIssues" :key="`${issue.code}-${issueIndex}`"><button type="button" @click="issue.questionIndex !== undefined && selectQuestion(issue.questionIndex)">{{ issue.message }}</button></li></ul></details>
            </div>
            <label class="builder-question-mobile"><span>当前编辑题目</span><select :value="activeQuestionIndex" @change="selectQuestion(Number(($event.target as HTMLSelectElement).value))"><option v-for="(question, index) in form.definition.questions" :key="question.id" :value="index">Q{{ String(index + 1).padStart(2, '0') }} · {{ question.prompt.slice(0, 28) || '尚未填写题干' }}</option></select></label>
            <article v-if="currentQuestion" :key="currentQuestion.id" class="question-editor">
              <div class="question-editor-head"><GripVertical/><b>Q{{ String(activeQuestionIndex + 1).padStart(2, '0') }}</b><select v-model="currentQuestion.type"><option value="slider">连续滑动</option><option value="scale">量表评分</option><option value="single">单选</option><option value="multi">多选</option><option value="matrix_single">矩阵单选</option><option value="matrix_multi">矩阵多选</option><option value="ranking">排序题</option><option value="number">数字</option><option value="date">日期</option><option value="text">开放文本</option></select><span/><button title="复制题目" aria-label="复制当前题目" @click="duplicateQuestion(activeQuestionIndex)"><Copy/></button><button class="danger-icon" title="删除题目" :aria-label="`删除第 ${activeQuestionIndex + 1} 题`" @click="removeQuestion(activeQuestionIndex)"><Trash2/></button></div>
              <textarea v-model="currentQuestion.prompt" rows="3" class="question-prompt-input"/>
              <div class="question-settings">
                <label><span>所属维度</span><select v-model="currentQuestion.dimension"><option value="">不计分</option><option v-for="dimension in form.definition.dimensions" :key="dimension.key" :value="dimension.key">{{ dimension.label }}</option></select></label>
                <label v-if="currentQuestion.type === 'slider' || currentQuestion.type === 'scale'"><span>左侧标签</span><input v-model="currentQuestion.leftLabel"/></label>
                <label v-if="currentQuestion.type === 'slider' || currentQuestion.type === 'scale'"><span>右侧标签</span><input v-model="currentQuestion.rightLabel"/></label>
                <label class="toggle-line"><input v-model="currentQuestion.required" type="checkbox"/><span>必答</span></label>
              </div>
              <label v-if="currentQuestion.type === 'single' || currentQuestion.type === 'multi' || currentQuestion.type === 'ranking'" class="field-label option-editor"><span>选项（每行一个）</span><textarea :value="optionText(currentQuestion)" rows="5" @input="setOptions(currentQuestion, ($event.target as HTMLTextAreaElement).value)"/></label>
              <div v-if="currentQuestion.type === 'matrix_single' || currentQuestion.type === 'matrix_multi'" class="form-grid"><label class="field-label"><span>矩阵行（每行一个）</span><textarea :value="listText(currentQuestion.rows)" rows="5" @input="setList(currentQuestion, 'rows', ($event.target as HTMLTextAreaElement).value)"/></label><label class="field-label"><span>矩阵列（每行一个）</span><textarea :value="listText(currentQuestion.columns)" rows="5" @input="setList(currentQuestion, 'columns', ($event.target as HTMLTextAreaElement).value)"/></label></div>
              <details class="question-advanced-settings">
                <summary><span><ShieldCheck/>高级题目设置</span><small>{{ currentQuestion.visibility || currentQuestion.randomizeOptions || currentQuestion.reverse || currentQuestion.crisisTriggers?.length ? '已配置' : '未启用' }}</small><ChevronDown/></summary>
                <div class="question-settings">
                  <label class="toggle-line" v-if="currentQuestion.dimension"><input v-model="currentQuestion.reverse" type="checkbox"/><span>反向计分</span></label>
                  <label v-if="currentQuestion.type === 'single' || currentQuestion.type === 'multi'" class="toggle-line"><input v-model="currentQuestion.randomizeOptions" type="checkbox"/><span>随机选项</span></label>
                  <label><span>显示条件</span><select :value="currentQuestion.visibility ? 'conditional' : ''" @change="setVisibility(currentQuestion, ($event.target as HTMLSelectElement).value ? form.definition.questions[0]?.id || '' : '')"><option value="">始终显示</option><option value="conditional" :disabled="activeQuestionIndex === 0">满足条件时显示</option></select></label>
                  <label v-if="currentQuestion.visibility"><span>多条件关系</span><select v-model="currentQuestion.visibility.mode"><option value="all">并且 · 全部满足</option><option value="any">或者 · 任一满足</option></select></label>
                  <label v-if="form.type === 'sensitive' && (currentQuestion.type === 'scale' || currentQuestion.type === 'single')" class="toggle-line"><input type="checkbox" :checked="Boolean(currentQuestion.crisisTriggers?.length)" @change="toggleCrisis(currentQuestion, ($event.target as HTMLInputElement).checked)"/><span>危机触发题</span></label>
                </div>
                <div v-if="currentQuestion.visibility" class="visibility-rule-editor">
                  <div class="visibility-flow-preview">
                    <span>{{ currentQuestion.visibility.mode === 'all' ? '同时满足' : '满足任一' }}</span>
                    <i/>
                    <strong>显示 Q{{ activeQuestionIndex + 1 }}</strong>
                  </div>
                  <article v-for="(rule, ruleIndex) in currentQuestion.visibility.rules" :key="`${rule.questionId}-${ruleIndex}`">
                    <b>{{ ruleIndex + 1 }}</b>
                    <label><span>来源题目</span><select v-model="rule.questionId"><option v-for="source in form.definition.questions.slice(0, activeQuestionIndex)" :key="source.id" :value="source.id">Q{{ form.definition.questions.indexOf(source) + 1 }} · {{ source.prompt.slice(0, 32) }}</option></select></label>
                    <label><span>条件</span><select v-model="rule.operator"><option value="equals">等于</option><option value="not_equals">不等于</option><option value="includes">包含</option><option value="not_includes">不包含</option><option value="greater_than">大于</option><option value="less_than">小于</option><option value="answered">已回答</option><option value="not_answered">未回答</option></select></label>
                    <label v-if="!['answered','not_answered'].includes(rule.operator)"><span>比较值</span><input v-model="rule.value"/></label>
                    <button type="button" title="删除这条条件" aria-label="删除显示条件" @click="removeVisibilityRule(currentQuestion, ruleIndex)"><Trash2/></button>
                  </article>
                  <button type="button" class="add-visibility-rule" @click="addVisibilityRule(currentQuestion)"><CirclePlus/>添加“{{ currentQuestion.visibility.mode === 'all' ? '并且' : '或者' }}”条件</button>
                  <p class="visibility-readable">当前逻辑：{{ currentQuestion.visibility.rules.map(rule => `${ruleQuestionNumber(rule.questionId)} ${ruleOperatorLabel(rule.operator)} ${['answered','not_answered'].includes(rule.operator) ? '' : String(rule.value ?? '')}`).join(currentQuestion.visibility.mode === 'all' ? '，并且 ' : '，或者 ') }} → 显示 Q{{ activeQuestionIndex + 1 }}</p>
                </div>
                <div v-if="currentQuestion.crisisTriggers?.length" class="crisis-rule-editor"><TriangleAlert/><label>触发阈值<input v-model.number="currentQuestion.crisisTriggers[0].value" type="number"/></label><label>严重度<select v-model="currentQuestion.crisisTriggers[0].severity"><option value="watch">关注</option><option value="urgent">紧急</option><option value="emergency">立即危险</option></select></label><small>触发规则不会展示给填写者。</small></div>
              </details>
            </article>
            <div class="question-switcher"><button type="button" :disabled="activeQuestionIndex === 0" @click="selectQuestion(activeQuestionIndex - 1)">上一题</button><span>Q{{ activeQuestionIndex + 1 }} / {{ form.definition.questions.length }}</span><button type="button" :disabled="activeQuestionIndex >= form.definition.questions.length - 1" @click="selectQuestion(activeQuestionIndex + 1)">下一题</button></div>
            <div class="add-question-row"><button @click="addQuestion('slider')">+ 连续滑动</button><button @click="addQuestion('scale')">+ 量表</button><button @click="addQuestion('single')">+ 单选</button><button @click="addQuestion('matrix_single')">+ 矩阵</button><button @click="addQuestion('ranking')">+ 排序</button><button @click="addQuestion('text')">+ 开放文本</button></div>
          </section>
        </template>

        <template v-else-if="activeTab === 'result'">
          <section class="builder-section">
            <div class="builder-section-title"><span>01</span><div><h2>计分维度</h2><p>每道计分题会归入一个维度，并换算为0—100分。</p></div><button class="button button-small" @click="addDimension"><CirclePlus/>添加维度</button></div>
            <div class="dimension-editor-grid"><article v-for="(dimension, dimensionIndex) in form.definition.dimensions" :key="dimension.key"><input v-model="dimension.color" type="color"/><label>名称<input v-model="dimension.label"/></label><label>内部键<input v-model="dimension.key"/></label><button class="profile-remove" title="删除维度" :aria-label="`删除维度 ${dimension.label}`" @click="removeDimension(dimensionIndex)"><Trash2/></button></article></div>
          </section>
          <section class="builder-section result-mode-section">
            <div class="builder-section-title"><span>02</span><div><h2>结果呈现方式</h2><p>这个标签会公开展示，填写者在开始前就能知道结果形式。</p></div></div>
            <div class="result-mode-grid">
              <button :class="{ selected: resultMode === 'profile' }" @click="resultMode = 'profile'"><span><Image/><b>含 IP 角色结果</b></span><p>展示角色名称、图片、描述和成长建议，适合娱乐探索与叙事体验。</p><i>{{ resultMode === 'profile' ? '当前选择' : '选择此模式' }}</i></button>
              <button :class="{ selected: resultMode === 'scores_only' }" @click="resultMode = 'scores_only'"><span><Layers3/><b>纯维度结果</b></span><p>只展示各维度分数与完成说明，减少标签和角色形象对判断的影响。</p><i>{{ resultMode === 'scores_only' ? '当前选择' : '选择此模式' }}</i></button>
            </div>
            <div class="mode-disclosure"><Info/><p><strong>填写前公开标签：</strong>{{ resultMode === 'profile' ? '含 IP 角色 · 创作者自定义形象' : '无 IP 角色 · 纯维度反馈' }}。平台会保留必要的内容处置权；创作者须对图片授权、角色表述和匹配逻辑负责。</p></div>
          </section>
          <section v-if="resultMode === 'profile'" class="builder-section profile-management-section">
            <div class="builder-section-title"><span>03</span><div><h2>IP 结果角色</h2><p>系统选择维度分数最接近的角色；研究用途会进入严格人工复核。</p></div><button class="button button-small" @click="addProfile"><CirclePlus/>添加角色</button></div>
            <div v-if="strictIpReview" class="strict-review-note"><ShieldCheck/><div><strong>研究/敏感模式：严格把关</strong><p>每个角色都要说明匹配依据；发布时会检查目标分数的区分度、参与者告知和伦理偏差确认，并进入人工审核。</p></div></div>
            <div class="profile-editor-grid">
              <article v-for="(profile, profileIndex) in form.definition.profiles as EditableProfile[]" :key="profile.key" :style="{ '--profile-color': profile.color }">
                <div class="profile-image-editor">
                  <img v-if="profileImage(profile)" :src="profileImage(profile)" :alt="`${profile.title}角色图`"/>
                  <div v-else><Image/><span>尚未上传角色图</span></div>
                  <label><input type="file" accept="image/jpeg,image/png,image/webp" :disabled="uploadingProfileKey === profile.key" @change="uploadProfileImage(profile, $event)"/><Image/>{{ uploadingProfileKey === profile.key ? '上传中…' : profileImage(profile) ? '更换图片' : '上传图片' }}</label>
                  <button v-if="profileImage(profile)" type="button" @click="removeProfileImage(profile)"><Trash2/>移除</button>
                </div>
                <div class="profile-editor-top"><input v-model="profile.color" type="color"/><input v-model="profile.emoji" class="emoji-input" aria-label="角色符号"/><input v-model="profile.title" class="profile-title-input" aria-label="角色名称"/><button class="profile-remove" type="button" aria-label="删除角色" @click="removeProfile(profileIndex)"><Trash2/></button></div>
                <label class="profile-field">角色描述<textarea v-model="profile.description" rows="3" placeholder="避免诊断、贬损、能力定性和绝对化判断。"/></label>
                <label class="profile-field">成长建议<textarea v-model="profile.advice" rows="2" placeholder="给出温和、具体、可选择的建议。"/></label>
                <label v-if="strictIpReview" class="profile-field rationale-field">匹配依据 <small>至少 20 字，仅供平台审核，不向填写者公开</small><textarea v-model="profile.matchRationale" rows="3" placeholder="说明哪些维度组合对应此角色，以及为何不会造成误导。"/></label>
                <div class="target-row"><label v-for="dimension in form.definition.dimensions" :key="dimension.key">{{ dimension.label }}<input v-model.number="profile.targets[dimension.key]" type="range" min="0" max="100"/><b>{{ profile.targets[dimension.key] ?? 50 }}</b></label></div>
              </article>
            </div>
            <div v-if="!form.definition.profiles.length" class="profile-empty"><Image/><h3>还没有结果角色</h3><p>添加角色后，可上传图片并设置它对应的维度目标。</p><button class="button" @click="addProfile"><CirclePlus/>添加第一个角色</button></div>
            <section v-if="form.definition.profiles.length" class="matching-simulator">
              <div class="simulator-heading"><div><span>04</span><h3>角色匹配模拟器</h3><p>调整模拟分数，检查不同结果角色是否有足够清晰的区分度。此工具不会保存或展示给填写者。</p></div><b v-if="profileSimulation.winner">当前匹配：{{ profileSimulation.winner.profile.title }}</b></div>
              <div class="simulation-controls"><label v-for="dimension in form.definition.dimensions" :key="dimension.key"><span>{{ dimension.label }} <b>{{ simulationScore(dimension.key) }}</b></span><input :value="simulationScore(dimension.key)" type="range" min="0" max="100" @input="setSimulationScore(dimension.key, ($event.target as HTMLInputElement).value)"/></label></div>
              <div v-if="profileSimulation.winner" :class="['simulation-result', { warning: !profileSimulation.clear }]">
                <div><strong>{{ profileSimulation.winner.profile.emoji }} {{ profileSimulation.winner.profile.title }}</strong><small>与目标组合的平均差异 {{ profileSimulation.winner.distance }} 分</small></div>
                <p v-if="profileSimulation.gap !== null">{{ profileSimulation.clear ? `与第二候选相差 ${profileSimulation.gap} 分，区分度清晰。` : `与第二候选只相差 ${profileSimulation.gap} 分，建议拉开目标分数或调整角色设定。` }}</p>
              </div>
              <div class="simulation-ranking"><span v-for="item in profileSimulation.ranked" :key="item.profile.key"><i :style="{ width: `${Math.max(8, 100 - item.distance)}%`, background: item.profile.color }"/><b>{{ item.profile.title }}</b><small>{{ item.distance }}</small></span></div>
            </section>
            <div class="ip-confirmations">
              <h3>发布前责任确认</h3>
              <label><input v-model="form.definition.governance!.ipRightsConfirmed" type="checkbox"/><span class="custom-check"><Check/></span><span><b>图片与设定权利</b><small>我拥有上传图片、角色名称与设定的合法使用权，不侵犯他人著作权、肖像权或商标权。</small></span></label>
              <label><input v-model="form.definition.governance!.ipSafetyConfirmed" type="checkbox"/><span class="custom-check"><Check/></span><span><b>内容安全</b><small>角色内容不违法、不歧视、不侮辱，也不冒充医学诊断或对填写者作绝对化定性。</small></span></label>
              <label><input v-model="form.definition.governance!.ipMatchConfirmed" type="checkbox"/><span class="custom-check"><Check/></span><span><b>匹配复核</b><small>我已逐项检查目标分数、角色描述与建议，确认其匹配逻辑清晰且不会故意误导。</small></span></label>
              <label v-if="strictIpReview"><input v-model="form.definition.governance!.ipBiasAcknowledged" type="checkbox"/><span class="custom-check"><Check/></span><span><b>研究偏差与伦理告知</b><small>我已在知情说明中告知角色结果可能产生标签效应，并评估其对实验判断与参与者体验的影响。</small></span></label>
              <p>创作者对其内容、授权与研究声明负责；平台仍依法保留审核、限制展示、下架和处理投诉的权利。</p>
            </div>
          </section>
          <section v-else class="builder-section scores-only-preview">
            <div><Layers3/><span>03</span></div><h2>纯维度结果已启用</h2><p>填写完成后只展示雷达图、各维度分数与完成说明，不展示角色名称、图片、角色描述和角色建议。已有角色草稿会保留，切换回来仍可继续编辑。</p>
          </section>
        </template>

        <template v-else-if="activeTab === 'experience'">
          <section class="builder-section community-publish-editor">
            <div class="builder-section-title"><span>01</span><div><h2>访问方式与展示卡片</h2><p>公开内容通过站长审核后进入内容馆；私密问卷只通过专属链接访问。</p></div><b :class="['community-zone-chip', strictIpReview ? 'research' : 'heartlight']">{{ publicationDestination }}</b></div>
            <div class="community-route-note">
              <Sparkles v-if="!strictIpReview"/><BookOpenCheck v-else/>
              <div>
                <strong>{{ form.settings.listingMode === 'unlisted' ? '私密问卷不会进入搜索与内容馆' : strictIpReview ? '科研与实验问卷申请进入量域馆' : '娱乐、情绪抒发与轻探索申请进入心光馆' }}</strong>
                <p>系统先检查伦理、隐私、诊断暗示与伤害风险，最终必须由站长人工审核；AI 结果仅作为审核建议。</p>
              </div>
            </div>
            <div class="result-filter publication-mode-picker" aria-label="问卷访问方式"><button type="button" :class="{ active: form.settings.listingMode !== 'unlisted' }" @click="form.settings.listingMode = 'public'">申请进入内容馆</button><button type="button" :class="{ active: form.settings.listingMode === 'unlisted' }" @click="form.settings.listingMode = 'unlisted'">不公开 · 专属链接</button></div>
            <label class="field-label wide"><span>问卷简短介绍 <small>{{ form.settings.listingMode === 'unlisted' ? '展示在专属链接详情页' : '展示在内容馆卡片上，建议 30—120 字' }}</small></span><textarea v-model="form.definition.governance!.communityIntro" rows="3" maxlength="300" placeholder="用自然、真诚的一段话告诉填写者：这份问卷在探索什么，适合谁。"/><small class="field-counter">{{ form.definition.governance?.communityIntro?.length || 0 }}/300</small></label>

            <div v-if="strictIpReview" class="research-self-assessment">
              <div class="impact-rating-head"><div><h3>对填写者的影响等级</h3><p>请结合题目敏感度、情绪负荷、标签效应与实验操纵如实自评。</p></div><b>{{ impactLabels[(form.definition.governance?.impactLevel || 1) - 1] }}</b></div>
              <div class="impact-stars" role="radiogroup" aria-label="对填写者影响等级">
                <button v-for="level in 5" :key="level" type="button" :class="{ active: level <= (form.definition.governance?.impactLevel || 1) }" :aria-label="`${level} 星：${impactLabels[level - 1]}`" @click="form.definition.governance!.impactLevel = level as 1 | 2 | 3 | 4 | 5"><Star/><span>{{ level }}</span></button>
              </div>
              <div class="self-assessment-heading"><BookOpenCheck/><div><h3>问卷自评书</h3><p>以下内容用于 AI 一致性核验和平台审核，不会完整公开给填写者。</p></div></div>
              <div class="form-grid">
                <label class="field-label wide"><span>研究目的</span><textarea v-model="form.definition.governance!.researchSelfAssessment!.researchPurpose" rows="3" placeholder="说明研究问题、使用场景与预期用途（至少 20 字）"/></label>
                <label class="field-label wide"><span>方法与量表依据</span><textarea v-model="form.definition.governance!.researchSelfAssessment!.methodBasis" rows="3" placeholder="说明题项来源、理论依据、改编方式或实验设计（至少 20 字）"/></label>
                <label class="field-label wide"><span>对参与者的可能影响</span><textarea v-model="form.definition.governance!.researchSelfAssessment!.participantImpact" rows="3" placeholder="说明可能产生的疲劳、情绪触动、标签效应或判断偏差（至少 20 字）"/></label>
                <label class="field-label wide"><span>风险缓解措施</span><textarea v-model="form.definition.governance!.researchSelfAssessment!.riskMitigation" rows="3" placeholder="说明退出机制、去欺骗说明、支持资源、数据最小化等措施（至少 20 字）"/></label>
              </div>
              <label class="assessment-confirm"><input v-model="form.definition.governance!.researchSelfAssessment!.truthfulnessConfirmed" type="checkbox"/><span class="custom-check"><Check/></span><span><b>真实性确认</b><small>我确认自评书与实际问卷、研究流程和伦理安排一致，并愿意对虚假或遗漏信息承担责任。</small></span></label>
            </div>
          </section>

          <section class="builder-section">
            <div class="builder-section-title"><span>02</span><div><h2>填写体验模式</h2><p>先确定问卷在填写过程中应当温暖回应，还是保持科研中立。</p></div></div>
            <div class="experience-mode-picker" role="radiogroup" aria-label="填写体验模式">
              <button type="button" :class="{ active: experienceMode === 'heartlight' }" role="radio" :aria-checked="experienceMode === 'heartlight'" @click="experienceMode = 'heartlight'">
                <Sparkles/><span><strong>心光体验模式</strong><small>适合娱乐与日常探索，保留动效、声音、积极反馈和 IP 结果。</small></span><Check/>
              </button>
              <button type="button" :class="{ active: experienceMode === 'neutral' }" role="radio" :aria-checked="experienceMode === 'neutral'" @click="experienceMode = 'neutral'">
                <FlaskConical/><span><strong>量域中立模式</strong><small>适合科研量表，默认关闭方向性反馈，强调知情同意与稳定排版。</small></span><Check/>
              </button>
            </div>
            <div class="builder-section-title experience-visual-heading"><span>03</span><div><h2>默认填写方式</h2><p>创作者设定首次打开时的方式，填写者仍可在问卷顶部随时切换。</p></div></div>
            <div class="fill-layout-picker" role="radiogroup" aria-label="默认填写方式">
              <button type="button" role="radio" :aria-checked="defaultFillMode === 'paged'" :class="{ active: defaultFillMode === 'paged' }" @click="defaultFillMode = 'paged'"><Layers3/><span><strong>逐题翻页</strong><small>一次专注一道题，适合沉浸体验和长情境题。</small></span><Check/></button>
              <button type="button" role="radio" :aria-checked="defaultFillMode === 'scroll'" :class="{ active: defaultFillMode === 'scroll' }" @click="defaultFillMode = 'scroll'"><Monitor/><span><strong>连续滚动</strong><small>上下浏览全部题目，适合熟悉传统表单的填写者。</small></span><Check/></button>
            </div>
            <div class="builder-section-title experience-visual-heading"><span>04</span><div><h2>视觉与声音</h2><p>填写者仍可以随时关闭声音、背景音乐和动效。</p></div></div>
            <div class="experience-grid basic-experience-grid"><article><Palette/><h3>问卷主色</h3><div class="accent-options"><button v-for="color in accents" :key="color" type="button" :class="{ selected: form.accent === color }" :style="{ background: color }" :title="`选择主题色 ${color}`" :aria-label="`选择主题色 ${color}`" :aria-pressed="form.accent === color" @click="form.accent = color"><Check/></button></div></article><article><Volume2/><h3>声音反馈</h3><p>选择、翻页、完成时使用中性合成音效，填写者可以随时关闭。</p><label class="large-toggle"><input v-model="form.definition.presentation!.positiveFeedback" type="checkbox"/><span/><b>{{ form.definition.presentation?.positiveFeedback ? '已开启' : '已关闭' }}</b></label></article></div>
            <details class="builder-inline-advanced">
              <summary><span><FlaskConical/>实验与随机化</span><small>{{ form.definition.experiment?.enabled || form.definition.presentation?.randomizeQuestions ? '已启用设置' : '按需展开' }}</small><ChevronDown/></summary>
              <div class="experience-grid"><article><Sparkles/><h3>A/B实验</h3><label class="large-toggle"><input v-model="form.definition.experiment!.enabled" type="checkbox"/><span/><b>{{ form.definition.experiment?.enabled ? '已开启' : '未开启' }}</b></label><p>随机比较连续滑动与五点选择体验。</p></article><article><Layers3/><h3>题目随机</h3><label class="large-toggle"><input v-model="form.definition.presentation!.randomizeQuestions" type="checkbox"/><span/><b>{{ form.definition.presentation?.randomizeQuestions ? '已开启' : '按顺序' }}</b></label><p>每位参与者获得稳定但不同的题目顺序。</p></article></div>
            </details>
          </section>
          <section class="builder-section publish-checklist">
            <div class="builder-section-title"><span>05</span><div><h2>{{ standaloneMode ? '本地保存检查' : '提交检查' }}</h2><p>{{ standaloneMode ? '此处内容仅保存到当前浏览器。需要公开时，请先导出项目 JSON，再交给管理员导入、复核和发布。' : '自动初筛会检查敏感内容、个人信息和同意说明，之后统一进入站长人工审核。' }}</p></div></div>
            <ul><li><Check/>体验模式：{{ experienceMode === 'heartlight' ? '心光体验' : '量域中立' }}</li><li><Check/>默认填写：{{ defaultFillMode === 'paged' ? '逐题翻页' : '连续滚动' }}</li><li><Check/>访问方式：{{ publicationDestination }}</li><li><Check/>已有 {{ form.definition.questions.length }} 道题</li><li><Check/>已有 {{ form.definition.dimensions.length }} 个计分维度</li><li><Check/>{{ resultMode === 'profile' ? `含 ${form.definition.profiles.length} 个 IP 结果角色` : '使用纯维度结果，不含 IP 角色' }}</li><li :class="{ warning: (form.definition.governance?.communityIntro?.length || 0) < 10 }"><Check/>问卷简短介绍</li><li v-if="strictIpReview" :class="{ warning: !form.definition.governance?.researchSelfAssessment?.truthfulnessConfirmed }"><Check/>科研自评书与真实性确认</li><li :class="{ warning: form.definition.consent.length < 15 }"><Check/>知情同意说明</li></ul>
            <label class="field-label"><span>内容风险自报</span><select v-model="form.riskLevel"><option value="low">低风险</option><option value="medium">中风险</option><option value="high">高风险</option></select></label>
            <button v-if="!standaloneMode" class="button button-dark" @click="save(true)"><Send/>保存并申请站长审核</button>
            <button v-else class="button button-dark" @click="save(false)"><Save/>保存到当前浏览器</button>
          </section>
        </template>

        <template v-else>
          <section class="builder-section">
            <div class="builder-section-title"><span>01</span><div><h2>回收规则</h2><p>控制配额、时间窗口、访问密码和重复填写。</p></div></div>
            <div class="form-grid"><label class="field-label"><span>回收配额（留空不限）</span><input v-model.number="form.settings.quota" type="number" min="1" placeholder="不限"/></label><label class="field-label"><span>重复填写</span><select v-model="form.settings.responseLimit"><option value="once">每人一次</option><option value="daily">每天一次</option><option value="unlimited">不限制</option></select></label><label class="field-label"><span>开始时间</span><input v-model="form.settings.startsAt" type="datetime-local"/></label><label class="field-label"><span>结束时间</span><input v-model="form.settings.endsAt" type="datetime-local"/></label><label class="field-label"><span>{{ form.settings.passwordProtected ? '更换访问密码（不填则不变）' : '访问密码（可选）' }}</span><input v-model="form.settings.password" type="password" placeholder="至少 4 位"/></label><label class="toggle-line"><input v-model="form.settings.clearPassword" type="checkbox"/><span>清除原访问密码</span></label><label class="toggle-line"><input v-model="form.settings.requireLogin" type="checkbox"/><span>要求填写者登录（仅特殊私密研究使用）</span></label><label class="toggle-line"><input v-model="form.settings.allowSaveProgress" type="checkbox"/><span>允许服务端保存进度</span></label></div>
            <p class="builder-setting-note"><ShieldCheck :size="16"/>公开问卷默认允许匿名填写：填写者打开分享链接即可作答，提交后答案自动进入你的数据中心。只有特殊私密研究才建议要求登录。</p>
          </section>

          <section class="builder-section">
            <div class="builder-section-title"><span>02</span><div><h2>心理内容治理声明</h2><p>声明不能替代审核材料；心理/敏感量表发布前需提交授权证据。</p></div></div>
            <div class="form-grid"><label class="field-label"><span>版权主体</span><input v-model="form.definition.governance!.copyrightOwner" placeholder="作者、机构或版权方"/></label><label class="field-label"><span>授权或许可说明</span><input v-model="form.definition.governance!.license" placeholder="许可类型、授权范围"/></label><label class="field-label wide"><span>适用人群与信效度说明</span><textarea v-model="form.definition.governance!.validatedPopulation" rows="3" placeholder="样本、常模、信度、效度及局限"/></label><label class="field-label wide"><span>危机处置协议</span><textarea v-model="form.definition.governance!.crisisProtocol" rows="3" placeholder="触发条件、参与者提示、人工响应与升级流程"/></label><label class="field-label"><span>数据保留天数</span><input v-model.number="form.definition.governance!.dataRetentionDays" type="number" min="1" max="3650"/></label><label class="toggle-line"><input v-model="form.definition.governance!.expertReviewRequired" type="checkbox"/><span>发布前必须通过专家审核</span></label></div>
          </section>

          <section v-if="id && !standaloneMode" class="builder-section">
            <div class="builder-section-title"><span>03</span><div><h2>审核材料</h2><p>平台只保存材料元数据与链接；建议外部文件使用受控访问。</p></div></div>
            <div class="professional-form"><select v-model="documentForm.kind"><option value="copyright_license">版权/授权</option><option value="expert_review">专家审核</option><option value="ethics_approval">伦理审批</option><option value="psychometric_report">信效度报告</option><option value="minor_protection">未成年人保护</option></select><input v-model="documentForm.title" placeholder="材料名称"/><input v-model="documentForm.issuer" placeholder="出具机构/专家"/><input v-model="documentForm.referenceUrl" type="url" placeholder="https:// 材料链接"/><button class="button" @click="addDocument">提交材料</button></div>
            <div class="document-list"><article v-for="doc in governanceDocuments" :key="doc.id"><ShieldCheck/><div><strong>{{ doc.title }}</strong><small>{{ doc.kind }} · {{ doc.issuer || '未填出具方' }}</small></div><span :class="doc.status">{{ doc.status }}</span><button v-if="doc.status !== 'verified'" title="删除材料" :aria-label="`删除材料 ${doc.title}`" @click="removeDocument(doc)"><Trash2/></button></article><p v-if="!governanceDocuments.length">尚未提交材料。</p></div>
          </section>

          <section v-if="id && !standaloneMode" class="builder-section">
            <div class="builder-section-title"><span>04</span><div><h2>多人协作</h2><p>编辑者可修改，分析者可看数据，审核者可看治理信息。</p></div></div>
            <div class="professional-form"><input v-model="collaboratorForm.email" type="email" placeholder="已注册用户邮箱"/><select v-model="collaboratorForm.role"><option value="editor">编辑者</option><option value="analyst">分析者</option><option value="reviewer">审核者</option></select><button class="button" @click="addCollaborator">添加/更新</button></div><div class="document-list"><article v-for="member in collaborators" :key="member.id"><Layers3/><div><strong>{{ member.name }}</strong><small>{{ member.email }}</small></div><span>{{ member.role }}</span><button title="移除协作者" :aria-label="`移除协作者 ${member.name}`" @click="removeCollaborator(member.id, member.name)"><Trash2/></button></article><p v-if="!collaborators.length">尚无协作者。</p></div>
          </section>

          <section v-if="id && !standaloneMode" class="builder-section">
            <div class="builder-section-title"><span>05</span><div><h2>信效度初检</h2><p>计算 Cronbach α、分半信度、题总相关和相关矩阵；小样本仅供参考。</p></div><button class="button" @click="analyzePsychometrics">生成报告</button></div><pre v-if="psychometricReport" class="report-preview">{{ JSON.stringify(psychometricReport, null, 2) }}</pre>
          </section>

          <section v-if="id && !standaloneMode" class="builder-section appeal-section">
            <div class="builder-section-title"><span>06</span><div><h2>申诉与处置反馈</h2><p>若问卷、角色图片或相关举报被平台处置，可在这里补充事实、授权或修改说明；处理记录会保留在后台。</p></div></div>
            <div class="appeal-form"><select v-model="appealForm.targetType"><option value="review_decision">问卷审核决定</option><option value="profile_image">角色图片处置</option><option value="report_action">举报处置</option></select><input v-model="appealForm.targetId" placeholder="可选：相关记录或图片 ID"/><textarea v-model="appealForm.message" rows="3" placeholder="说明你希望复核的事实、已采取的修改和可提供的材料（至少 20 字）"/><button class="button" @click="submitAppeal">提交申诉</button></div>
            <div class="document-list"><article v-for="appeal in appeals" :key="appeal.id"><Info/><div><strong>{{ appeal.target_type }} · {{ appeal.status }}</strong><small>{{ appeal.message }}</small><small v-if="appeal.response_note">平台回复：{{ appeal.response_note }}</small></div></article><p v-if="!appeals.length">目前没有申诉记录。</p></div>
          </section>

          <section v-if="!id" class="builder-section security-notice"><Info/><p>请先保存草稿，再添加协作者、审核材料和生成信效度报告。</p></section>
        </template>
      </main>

      <button
        v-if="compactPreviewOpen"
        type="button"
        class="builder-preview-backdrop"
        aria-label="关闭实时预览"
        @click="compactPreviewOpen = false"
      />
      <aside id="builder-live-preview" :class="['builder-preview', { open: compactPreviewOpen }]">
        <div class="builder-preview-panel-head">
          <span><Eye/>实时移动端预览</span>
          <button type="button" aria-label="关闭实时预览" title="关闭实时预览" @click="compactPreviewOpen = false"><X/></button>
        </div>
        <div class="preview-device interactive-preview" :style="{ '--accent': form.accent }">
          <div class="preview-status"><span>9:41</span><i/></div>
          <div class="preview-brand">LUMISCALE <small>{{ defaultFillMode === 'paged' ? '逐题预览' : '滚动预览' }}</small></div>
          <div class="preview-progress"><span :style="{ width: `${Math.round((activeQuestionIndex + 1) / Math.max(form.definition.questions.length, 1) * 100)}%`, background: form.accent }"/></div>
          <div v-if="currentQuestion" class="preview-renderer">
            <SurveyQuestionRenderer :question="currentQuestion" :index="activeQuestionIndex" :total="form.definition.questions.length" :dimensions="form.definition.dimensions" :model-value="previewAnswers[currentQuestion.id]" instance-id="builder-phone" @update:model-value="setPreviewAnswer(currentQuestion, $event)"/>
          </div>
          <div class="preview-device-footer"><button type="button" :disabled="activeQuestionIndex === 0" @click="selectQuestion(activeQuestionIndex - 1)">上一题</button><button type="button" :style="{ background: form.accent }" :disabled="activeQuestionIndex >= form.definition.questions.length - 1" @click="selectQuestion(activeQuestionIndex + 1)">下一题 <ArrowRight/></button></div>
        </div>
        <button type="button" class="open-full-preview" @click="openFullPreview"><Eye/>打开完整填写预览</button>
        <p>这里可以直接点击选项，内容与正式填写页共用渲染组件</p>
      </aside>
    </div>
    <div v-if="fullPreviewOpen" class="builder-preview-overlay" role="dialog" aria-modal="true" aria-label="完整问卷填写预览" @keydown.esc="fullPreviewOpen = false">
      <header><div><p>PREVIEW · 不会保存为真实答卷</p><h2>{{ form.title }}</h2></div><div class="preview-overlay-actions"><button type="button" :class="{ active: defaultFillMode === 'paged' }" @click="defaultFillMode = 'paged'">逐题翻页</button><button type="button" :class="{ active: defaultFillMode === 'scroll' }" @click="defaultFillMode = 'scroll'">连续滚动</button><button type="button" class="preview-close" aria-label="关闭完整预览" @click="fullPreviewOpen = false"><X/></button></div></header>
      <div class="builder-desktop-preview" :class="`${defaultFillMode}-mode`" :style="{ '--accent': form.accent }">
        <aside><strong>答题地图</strong><small>{{ Object.keys(previewAnswers).length }} / {{ previewVisibleQuestions.length }}</small><div><button v-for="(item, index) in previewVisibleQuestions" :key="item.id" type="button" :class="{ active: index === fullPreviewIndex, answered: previewAnswers[item.id] !== undefined }" @click="fullPreviewIndex = index">{{ String(index + 1).padStart(2, '0') }}</button></div></aside>
        <main v-if="defaultFillMode === 'paged' && fullPreviewQuestion">
          <SurveyQuestionRenderer :question="fullPreviewQuestion" :index="fullPreviewIndex" :total="previewVisibleQuestions.length" :dimensions="form.definition.dimensions" :model-value="previewAnswers[fullPreviewQuestion.id]" instance-id="builder-desktop-paged" @update:model-value="setPreviewAnswer(fullPreviewQuestion, $event)"/>
        </main>
        <main v-else class="builder-preview-stream">
          <SurveyQuestionRenderer v-for="(item, index) in previewVisibleQuestions" :id="`builder-preview-question-${item.id}`" :key="item.id" :question="item" :index="index" :total="previewVisibleQuestions.length" :dimensions="form.definition.dimensions" :model-value="previewAnswers[item.id]" :instance-id="`builder-desktop-scroll-${index}`" @update:model-value="setPreviewAnswer(item, $event)"/>
        </main>
        <footer v-if="defaultFillMode === 'paged'"><button type="button" :disabled="fullPreviewIndex === 0" @click="moveFullPreview(-1)">上一题</button><span>预览进度 {{ fullPreviewIndex + 1 }} / {{ previewVisibleQuestions.length }}</span><button type="button" :disabled="fullPreviewIndex >= previewVisibleQuestions.length - 1" @click="moveFullPreview(1)">下一题<ArrowRight/></button></footer>
      </div>
    </div>
  </div>
</template>
